import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Loader2, Music2, Search, ArrowLeft, Upload, Youtube, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import SpotifyTestUserOnboarding from "./SpotifyTestUserOnboarding";
import SeratoImport from "./SeratoImport";

const API = `${BACKEND_URL}/api`;

export default function TrackLibrary({ decks, loadTrackToDeck }) {
  const [demoTracks, setDemoTracks] = useState([]);
  const [demoFilter, setDemoFilter] = useState("");
  const [localTracks, setLocalTracks] = useState([]);
  const [libTab, setLibTab] = useState("demo");
  const localInputRef = React.useRef(null);
  const createdUrlsRef = React.useRef([]);

  useEffect(() => {
    const openPicker = () => {
      setLibTab("demo");
      // Wait a tick so the tab switch commits before we click the hidden input
      setTimeout(() => localInputRef.current && localInputRef.current.click(), 40);
      // Smooth-scroll to the panel so the DJ sees the imported tracks land
      setTimeout(() => {
        const panel = document.querySelector("[data-testid='track-library-panel']");
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    };
    window.addEventListener("nd-browse-local", openPicker);
    return () => window.removeEventListener("nd-browse-local", openPicker);
  }, []);

  // Revoke every blob URL we ever created — ONLY on unmount (empty deps).
  useEffect(() => () => {
    createdUrlsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    createdUrlsRef.current = [];
  }, []);

  const [aiVibe, setAiVibe] = useState("energetic");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  const [spotifyStatus, setSpotifyStatus] = useState({ connected: false });
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [spotifySearchQ, setSpotifySearchQ] = useState("");
  const [spotifySearchResults, setSpotifySearchResults] = useState([]);
  const [spotifySearchLoading, setSpotifySearchLoading] = useState(false);
  const [spotifyView, setSpotifyView] = useState("playlists");
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [spotifyTracks, setSpotifyTracks] = useState([]);
  const [spotifyLoading, setSpotifyLoading] = useState(false);

  // ---------- YouTube discovery tab (iter45) ----------
  // YouTube tracks are DISCOVERY ONLY — YouTube's TOS forbids extracting
  // audio streams so we can't route them through the Web Audio mixer.
  // The tab shows real search results with a Preview button that opens
  // a nocookie iframe modal so the DJ can vet the track before finding
  // the MP3 elsewhere and uploading it via the Library tab.
  const [ytQuery, setYtQuery] = useState("");
  const [ytResults, setYtResults] = useState([]);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytPreviewId, setYtPreviewId] = useState(null); // video id currently in the preview modal
  const [ytPreviewTitle, setYtPreviewTitle] = useState("");
  const [ytError, setYtError] = useState(null);
  const [ytBpmFilter, setYtBpmFilter] = useState(""); // "" | "slow" (60-100) | "mid" (100-125) | "fast" (125-140) | "hard" (140+)

  const _formatDur = (secs) => {
    if (!secs && secs !== 0) return null;
    const m = Math.floor(secs / 60);
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const _bpmInBand = (bpm, band) => {
    if (!band) return true;
    if (bpm == null) return band === "any";
    if (band === "slow") return bpm >= 60 && bpm < 100;
    if (band === "mid")  return bpm >= 100 && bpm < 125;
    if (band === "fast") return bpm >= 125 && bpm < 140;
    if (band === "hard") return bpm >= 140;
    return true;
  };

  const filteredYtResults = ytResults.filter(t => _bpmInBand(t.bpm, ytBpmFilter));

  const runYoutubeSearch = async (e) => {
    if (e) e.preventDefault();
    const q = ytQuery.trim();
    if (!q) return;
    setYtLoading(true);
    setYtError(null);
    try {
      const r = await axios.get(`${API}/tracks/youtube/search`, { params: { q } });
      setYtResults(r.data || []);
      if (!r.data?.length) setYtError("No results — try a different query.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "YouTube search failed";
      setYtError(detail);
      // 503 = key not set, 429 = quota, 403 = key rejected — all worth surfacing to the user
      toast.error(detail);
    } finally {
      setYtLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/tracks/demo`).then(r => setDemoTracks(r.data)).catch(() => {});
    checkSpotify();
    // eslint-disable-next-line
  }, []);

  const checkSpotify = async () => {
    try {
      const r = await axios.get(`${API}/spotify/status`);
      setSpotifyStatus(r.data);
      if (r.data.connected) fetchPlaylists();
    } catch { /* endpoint not ready yet */ }
  };

  const connectSpotify = async () => {
    try {
      const r = await axios.get(`${API}/spotify/login`);
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spotify not configured yet");
    }
  };

  const fetchPlaylists = async () => {
    setSpotifyLoading(true);
    try {
      const r = await axios.get(`${API}/spotify/playlists`);
      setSpotifyPlaylists([{ id: "__saved__", name: "Your Saved Tracks", track_count: null, artwork_url: null, owner: "You" }, ...r.data]);
    } catch { toast.error("Failed to load playlists"); }
    finally { setSpotifyLoading(false); }
  };

  const openPlaylist = async (pl) => {
    setSelectedPlaylist(pl);
    setSpotifyView("tracks");
    setSpotifyLoading(true);
    setSpotifyTracks([]);
    try {
      const url = pl.id === "__saved__" ? `${API}/spotify/library` : `${API}/spotify/playlists/${pl.id}/tracks`;
      const r = await axios.get(url);
      setSpotifyTracks(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load tracks"); }
    finally { setSpotifyLoading(false); }
  };

  const backToPlaylists = () => {
    setSpotifyView("playlists");
    setSelectedPlaylist(null);
    setSpotifyTracks([]);
  };

  const runSpotifySearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = spotifySearchQ.trim();
    if (!q) return;
    setSpotifySearchLoading(true);
    try {
      const r = await axios.get(`${API}/spotify/search`, { params: { q, limit: 10 } });
      setSpotifySearchResults(r.data || []);
      if ((r.data || []).length === 0) toast.info("No matches on Spotify.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Spotify search failed");
    } finally { setSpotifySearchLoading(false); }
  };


  const askAI = async () => {
    const playing = ["A", "B", "C", "D"].map(id => decks[id]).find(d => d.playing && d.track) || decks.A;
    if (!playing.track) { toast.error("Load & play a track on Deck A first"); return; }
    setAiLoading(true);
    try {
      const library = [...demoTracks, ...spotifyTracks].map(t => ({
        id: t.id, title: t.title, artist: t.artist, bpm: t.bpm, key: t.key,
        source: t.source, source_id: t.source_id, artwork_url: t.artwork_url,
      }));
      const r = await axios.post(`${API}/ai/recommend`, {
        current_bpm: (playing.track.bpm || 120) * (1 + playing.pitch / 100),
        current_key: playing.track.key || "8A",
        library, vibe: aiVibe,
      });
      setAiResults(r.data);
    } catch { toast.error("AI request failed"); }
    finally { setAiLoading(false); }
  };

  const TrackRow = ({ t, testid }) => (
    <div className="flex items-center gap-3 p-2 rounded-lg border border-[#1E2638] hover:border-[#2A354B] hover:bg-[#1A202C]/50 transition-colors" data-testid={testid}>
      {t.artwork_url ? (
        <img src={t.artwork_url} alt="" className="w-10 h-10 rounded object-cover"/>
      ) : <div className="w-10 h-10 rounded bg-[#0B0E14] border border-[#1E2638]"/>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate">{t.title}</div>
        <div className="text-[11px] text-[#94A3B8] truncate">{t.artist} · {t.bpm ? `${t.bpm} BPM` : "— BPM"} · {t.key || "—"}</div>
        {t.match_score != null && (
          <div className="text-[10px] font-mono text-[#10B981] uppercase tracking-widest">Match {t.match_score}% · Δ {t.bpm_diff} BPM</div>
        )}
        {t.source === "spotify" && !t.source_id && (
          <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">No preview — upload MP3 to mix</div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {["A", "B", "C", "D"].map(d => (
          <button key={d} onClick={() => loadTrackToDeck(d, t)}
            data-testid={`load-deck-${d.toLowerCase()}-${t.id}`}
            className="text-[10px] font-black px-2 py-0.5 rounded border border-[#2A354B] hover:border-white hover:bg-white hover:text-black transition-colors"
          >{d}</button>
        ))}
      </div>
    </div>
  );

  // Revoke any created object URLs when the component unmounts so we don't leak blobs.
  // See createdUrlsRef unmount cleanup above; this comment is a legacy marker.

  const onBrowseLocal = () => {
    if (localInputRef.current) localInputRef.current.click();
  };

  const onLocalFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = files.map(f => {
      const url = URL.createObjectURL(f);
      createdUrlsRef.current.push(url);
      return {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: f.name.replace(/\.[^.]+$/, ""),
        artist: "Local File",
        source: "local",
        source_id: url,
        bpm: null,
        key: null,
        artwork_url: null,
      };
    });
    setLocalTracks(prev => [...added, ...prev]);
    toast.success(`Added ${added.length} track${added.length === 1 ? "" : "s"} to your library`);
    // Reset the input so the same file can be re-selected later
    if (e.target) e.target.value = "";
  };

  const filteredDemo = demoTracks.filter(t => !demoFilter || t.title.toLowerCase().includes(demoFilter.toLowerCase()) || t.artist.toLowerCase().includes(demoFilter.toLowerCase()));
  const filteredLocal = localTracks.filter(t => !demoFilter || t.title.toLowerCase().includes(demoFilter.toLowerCase()));

  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-4 h-full flex flex-col relative z-10" data-testid="track-library-panel">
      <Tabs value={libTab} onValueChange={setLibTab} className="flex-1 flex flex-col">
        <TabsList className="grid grid-cols-4 bg-[#0B0E14] border border-[#1E2638]">
          <TabsTrigger value="demo" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black font-cond uppercase tracking-widest text-xs" data-testid="local-files-tab-button">Library</TabsTrigger>
          <TabsTrigger value="spotify" className="data-[state=active]:bg-[#1DB954] data-[state=active]:text-black font-cond uppercase tracking-widest text-xs" data-testid="spotify-tab-button">Spotify</TabsTrigger>
          <TabsTrigger value="youtube" className="data-[state=active]:bg-[#FF0000] data-[state=active]:text-white font-cond uppercase tracking-widest text-xs" data-testid="youtube-tab-button">YouTube</TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white font-cond uppercase tracking-widest text-xs" data-testid="ai-recommendations-tab-button">AI Pick</TabsTrigger>
        </TabsList>

        <TabsContent value="demo" className="flex-1 overflow-y-auto space-y-2 pt-3 pr-1">
          <button data-testid="browse-local-disk-button" onClick={onBrowseLocal}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-[#00F0FF]/40 hover:border-[#00F0FF] hover:bg-[#00F0FF]/5 text-[#00F0FF] font-black uppercase tracking-widest text-xs transition-colors">
            <Upload className="w-4 h-4"/> Browse Local Disk
          </button>
          <input ref={localInputRef} data-testid="local-files-input" type="file" accept="audio/*" multiple onChange={onLocalFiles} className="hidden"/>
          <SeratoImport onLocalFiles={onLocalFiles}/>
          <div className="flex gap-2 mb-2">
            <Input data-testid="track-search-input" placeholder="Filter library…" value={demoFilter} onChange={e => setDemoFilter(e.target.value)} className="bg-[#0B0E14] border-[#2A354B] text-white"/>
            <div className="flex items-center px-3 text-[#94A3B8]"><Search className="w-4 h-4"/></div>
          </div>
          {filteredLocal.length > 0 && (
            <div className="space-y-2" data-testid="local-tracks-section">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] pt-1">// Your Local Files ({filteredLocal.length})</div>
              {filteredLocal.map(t => <TrackRow key={t.id} t={t} testid={`local-track-${t.id}`}/>)}
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] pt-3">// Demo Library</div>
            </div>
          )}
          {filteredDemo.map(t => <TrackRow key={t.id} t={t} testid={`demo-track-${t.id}`}/>)}
        </TabsContent>

        <TabsContent value="spotify" className="flex-1 overflow-y-auto space-y-2 pt-3 pr-1">
          {!spotifyStatus.configured ? (
            <div className="p-6 text-center space-y-3">
              <Music2 className="w-10 h-10 text-[#1DB954] mx-auto"/>
              <div className="font-display font-black text-lg">Spotify not configured</div>
              <div className="text-xs text-[#94A3B8]">Provide your Spotify Client ID & Secret to enable live playlist import.</div>
            </div>
          ) : !spotifyStatus.connected ? (
            <div className="p-6 text-center space-y-3">
              <Music2 className="w-10 h-10 text-[#1DB954] mx-auto"/>
              <div className="font-display font-black text-lg">Connect Spotify</div>
              <div className="text-xs text-[#94A3B8]">Login to browse your saved tracks and every playlist you own or follow.</div>
              <Button data-testid="spotify-connect-button" onClick={connectSpotify} className="bg-[#1DB954] text-black hover:bg-[#1ed760] font-black uppercase tracking-widest">Connect Spotify</Button>
            </div>
          ) : spotifyView === "playlists" ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono text-[#94A3B8] uppercase tracking-widest">Logged in as {spotifyStatus.display_name}</div>
                <Button data-testid="spotify-refresh-button" onClick={fetchPlaylists} variant="ghost" className="text-xs h-7">
                  {spotifyLoading ? <Loader2 className="w-3 h-3 animate-spin"/> : "Refresh"}
                </Button>
              </div>
              <div className="mb-3 p-3 rounded-lg border border-[#FFC107]/40 bg-[#FFC107]/5 text-[11px] leading-relaxed" data-testid="spotify-dev-mode-notice">
                <div className="text-[#FFC107] font-mono uppercase tracking-widest text-[10px] mb-1">⚠ Spotify Developer-Mode limit</div>
                <div className="text-[#F8FAFC] mb-2">
                  Spotify blocks playlist track fetches for Development-Mode apps (since May 2025).
                  <span className="text-[#00F0FF]"> Your Saved Tracks + Global Search (up to 10 results) still work.</span>
                </div>
                <div className="text-[#94A3B8] mb-2 text-[10.5px]">
                  Full access now requires <b className="text-white">Extended Quota Mode</b>, which Spotify only approves for organizations with a live commercial product — the self-service button is retired.
                  In the meantime you can add up to <b className="text-white">5 individual Spotify accounts</b> as test users on the dashboard (they'll get full playlist + search access).
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <a href={spotifyStatus.client_id
                        ? `https://developer.spotify.com/dashboard/${spotifyStatus.client_id}/users`
                        : "https://developer.spotify.com/dashboard"} target="_blank" rel="noopener noreferrer"
                     data-testid="spotify-add-test-users-link"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#1DB954] text-black hover:bg-[#1ed760] font-black uppercase tracking-widest text-[10px]">
                    Add test users →
                  </a>
                  <a href="https://developer.spotify.com/documentation/web-api/concepts/quota-modes" target="_blank" rel="noopener noreferrer"
                     data-testid="spotify-extended-quota-link"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#1DB954] text-[#1DB954] hover:bg-[#1DB954]/10 font-black uppercase tracking-widest text-[10px]">
                    Apply for Extended Quota
                  </a>
                </div>
                {/* Onboarding widget — 5 slot tracker + copyable invite email so
                    friends know exactly what to send back and what they'll get. */}
                <SpotifyTestUserOnboarding />
              </div>
              <form onSubmit={runSpotifySearch} className="flex gap-2 mb-3" data-testid="spotify-search-form">
                <Input data-testid="spotify-search-input"
                  value={spotifySearchQ}
                  onChange={e => setSpotifySearchQ(e.target.value)}
                  placeholder="Search Spotify (any song, any artist)…"
                  className="bg-[#0B0E14] border-[#2A354B] text-white flex-1"/>
                <Button data-testid="spotify-search-button" type="submit" disabled={spotifySearchLoading || !spotifySearchQ.trim()}
                  className="bg-[#1DB954] text-black hover:bg-[#1ed760] font-black uppercase tracking-widest px-3">
                  {spotifySearchLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
                </Button>
              </form>
              {spotifySearchResults.length > 0 && (
                <div className="mb-3 space-y-1" data-testid="spotify-search-results">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#1DB954]">// Search results ({spotifySearchResults.length})</div>
                    <button type="button" data-testid="spotify-search-clear" onClick={() => setSpotifySearchResults([])}
                      className="text-[10px] font-mono text-[#94A3B8] hover:text-white uppercase tracking-widest">✕ clear</button>
                  </div>
                  <div className="text-[10px] text-[#94A3B8]/80 italic px-1" data-testid="spotify-search-preview-note">
                    Note: since 2024 most Spotify catalog tracks no longer expose a 30-second preview. Rows marked
                    "No preview — upload MP3 to mix" cannot be streamed inside the deck — use them as a shortlist
                    and upload the audio locally to mix.
                  </div>
                  {spotifySearchResults.map(t => <TrackRow key={t.id} t={t} testid={`spotify-search-track-${t.id}`}/>)}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] pt-2">// Your Playlists</div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2">
                {spotifyPlaylists.map(pl => (
                  <button key={pl.id} data-testid={`spotify-playlist-${pl.id}`} onClick={() => openPlaylist(pl)}
                    className="flex items-center gap-3 p-2 rounded-lg border border-[#1E2638] hover:border-[#1DB954] hover:bg-[#1DB954]/5 text-left transition-colors">
                    {pl.artwork_url ? <img src={pl.artwork_url} alt="" className="w-10 h-10 rounded object-cover"/> : <div className="w-10 h-10 rounded bg-[#1DB954]/10 border border-[#1DB954]/30 flex items-center justify-center"><Music2 className="w-4 h-4 text-[#1DB954]"/></div>}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{pl.name}</div>
                      <div className="text-[11px] text-[#94A3B8] truncate">{pl.owner}{pl.track_count != null ? ` · ${pl.track_count} tracks` : ""}</div>
                    </div>
                  </button>
                ))}
                {!spotifyLoading && spotifyPlaylists.length === 0 && <div className="text-xs text-[#94A3B8] text-center py-6">No playlists.</div>}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Button data-testid="spotify-back-button" onClick={backToPlaylists} variant="ghost" className="h-7 text-xs text-[#94A3B8] hover:text-white"><ArrowLeft className="w-3 h-3 mr-1"/> Playlists</Button>
                <div className="text-sm font-bold truncate flex-1">{selectedPlaylist?.name}</div>
                {spotifyLoading && <Loader2 className="w-3 h-3 animate-spin"/>}
              </div>
              <form onSubmit={runSpotifySearch} className="flex gap-2 mb-3" data-testid="spotify-search-form-tracks">
                <Input data-testid="spotify-search-input-tracks"
                  value={spotifySearchQ}
                  onChange={e => setSpotifySearchQ(e.target.value)}
                  placeholder="Search all of Spotify…"
                  className="bg-[#0B0E14] border-[#2A354B] text-white flex-1 h-8 text-xs"/>
                <Button data-testid="spotify-search-button-tracks" type="submit" disabled={spotifySearchLoading || !spotifySearchQ.trim()}
                  className="bg-[#1DB954] text-black hover:bg-[#1ed760] font-black uppercase tracking-widest px-3 h-8">
                  {spotifySearchLoading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Search className="w-3 h-3"/>}
                </Button>
              </form>
              {spotifySearchResults.length > 0 && (
                <div className="mb-3 space-y-1" data-testid="spotify-search-results-tracks">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#1DB954]">// Search results ({spotifySearchResults.length})</div>
                    <button type="button" data-testid="spotify-search-clear-tracks" onClick={() => setSpotifySearchResults([])}
                      className="text-[10px] font-mono text-[#94A3B8] hover:text-white uppercase tracking-widest">✕ clear</button>
                  </div>
                  <div className="text-[10px] text-[#94A3B8]/80 italic px-1" data-testid="spotify-search-preview-note-tracks">
                    Note: since 2024 most Spotify catalog tracks no longer expose a 30-second preview. Rows marked
                    "No preview — upload MP3 to mix" cannot be streamed inside the deck — use them as a shortlist
                    and upload the audio locally to mix.
                  </div>
                  {spotifySearchResults.map(t => <TrackRow key={t.id} t={t} testid={`spotify-search-track-${t.id}`}/>)}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] pt-2">// Playlist Tracks</div>
                </div>
              )}
              {spotifyTracks.map(t => <TrackRow key={t.id} t={t} testid={`spotify-track-${t.id}`}/>)}
              {!spotifyLoading && spotifyTracks.length === 0 && <div className="text-xs text-[#94A3B8] text-center py-6">No tracks in this playlist.</div>}
            </>
          )}
        </TabsContent>

        <TabsContent value="youtube" className="flex-1 overflow-y-auto space-y-2 pt-3 pr-1">
          <form onSubmit={runYoutubeSearch} className="flex gap-2" data-testid="youtube-search-form">
            <Input data-testid="youtube-search-input"
              placeholder="Search YouTube… (e.g. amapiano 2026)"
              value={ytQuery} onChange={e => setYtQuery(e.target.value)}
              className="bg-[#0B0E14] border-[#2A354B] text-white"/>
            <Button data-testid="youtube-search-button" type="submit" disabled={ytLoading || !ytQuery.trim()}
              className="bg-[#FF0000] text-white hover:bg-[#CC0000] font-black uppercase tracking-widest text-xs">
              {ytLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
            </Button>
          </form>
          <div className="p-3 rounded-lg border border-[#FF0000]/30 bg-[#FF0000]/5 text-[11px] leading-relaxed" data-testid="youtube-discovery-notice">
            <div className="font-mono uppercase tracking-widest text-[#FF0000] mb-1 text-[10px]">// Discovery only</div>
            YouTube's TOS forbids extracting audio streams, so these tracks <strong>cannot be mixed inside a deck</strong>. Use them to shortlist songs — tap Preview to hear the video in a mini player, then find the MP3 and upload it via the <strong>Library</strong> tab to actually mix it.
          </div>

          {ytError && !ytLoading && (
            <div className="text-[11px] text-[#EF4444] p-2" data-testid="youtube-error">{ytError}</div>
          )}

          {ytResults.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="youtube-bpm-filter">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mr-1">BPM:</span>
              {[
                { id: "", label: "All" },
                { id: "slow", label: "60-100" },
                { id: "mid",  label: "100-125" },
                { id: "fast", label: "125-140" },
                { id: "hard", label: "140+" },
              ].map(b => (
                <button
                  key={b.id || "all"}
                  type="button"
                  onClick={() => setYtBpmFilter(b.id)}
                  data-testid={`youtube-bpm-${b.id || "all"}`}
                  className={`text-[10px] font-black px-2 py-0.5 rounded border transition-colors ${
                    ytBpmFilter === b.id
                      ? "border-[#FF0000] bg-[#FF0000] text-white"
                      : "border-[#2A354B] text-[#94A3B8] hover:border-[#FF0000] hover:text-white"
                  }`}
                >{b.label}</button>
              ))}
              {ytBpmFilter && (
                <span className="text-[10px] font-mono text-[#94A3B8] ml-1" data-testid="youtube-bpm-count">
                  {filteredYtResults.length}/{ytResults.length}
                </span>
              )}
            </div>
          )}

          {filteredYtResults.map(t => (
            <div key={t.id}
                 className="flex items-center gap-3 p-2 rounded-lg border border-[#1E2638] hover:border-[#FF0000]/50 hover:bg-[#FF0000]/5 transition-colors"
                 data-testid={`youtube-track-${t.id}`}>
              {t.artwork_url ? (
                <img src={t.artwork_url} alt="" className="w-16 h-10 rounded object-cover shrink-0"/>
              ) : <div className="w-16 h-10 rounded bg-[#0B0E14] border border-[#1E2638] shrink-0"/>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{t.title}</div>
                <div className="text-[11px] text-[#94A3B8] truncate flex items-center gap-1.5">
                  <span className="truncate">{t.artist}</span>
                  {t.duration != null && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="font-mono shrink-0" data-testid={`youtube-dur-${t.id}`}>{_formatDur(t.duration)}</span>
                    </>
                  )}
                  {t.bpm != null && (
                    <span className="font-mono font-black px-1.5 py-0.5 rounded bg-[#FF0000]/15 text-[#FF0000] shrink-0 text-[10px] uppercase tracking-widest"
                          data-testid={`youtube-bpm-chip-${t.id}`}
                          title="BPM guessed from video title">
                      ~{t.bpm} BPM
                    </span>
                  )}
                </div>
              </div>
              <button type="button"
                onClick={() => { setYtPreviewId(t.source_id); setYtPreviewTitle(t.title); }}
                data-testid={`youtube-preview-${t.id}`}
                className="shrink-0 flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded border border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000] hover:text-white transition-colors uppercase tracking-widest">
                <Youtube className="w-3 h-3"/> Preview
              </button>
              <a href={`https://www.youtube.com/watch?v=${t.source_id}`} target="_blank" rel="noopener noreferrer"
                className="shrink-0 text-[#94A3B8] hover:text-white transition-colors"
                data-testid={`youtube-open-${t.id}`}
                title="Open on YouTube">
                <ExternalLink className="w-3.5 h-3.5"/>
              </a>
            </div>
          ))}
          {!ytResults.length && !ytLoading && !ytError && (
            <div className="text-xs text-[#94A3B8] text-center py-8 italic">Search YouTube to shortlist tracks for your next set.</div>
          )}
          {ytResults.length > 0 && filteredYtResults.length === 0 && (
            <div className="text-xs text-[#94A3B8] text-center py-6 italic" data-testid="youtube-empty-after-filter">
              None of the results match this BPM band. Try a different band or search again.
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai" className="flex-1 overflow-y-auto space-y-2 pt-3 pr-1">
          <div className="flex gap-2 mb-2">
            <select value={aiVibe} onChange={e => setAiVibe(e.target.value)} data-testid="ai-vibe-select" className="flex-1 bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded text-sm">
              <option value="energetic">Energetic</option>
              <option value="deep">Deep & Hypnotic</option>
              <option value="peak-time">Peak-Time</option>
              <option value="chill">Chill Out</option>
            </select>
            <Button data-testid="ai-recommend-button" onClick={askAI} className="bg-[#FF2A6D] text-white hover:bg-[#FF5588] font-black uppercase tracking-widest text-xs">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Sparkles className="w-4 h-4 mr-1"/> Ask AI</>}
            </Button>
          </div>
          {aiResults?.ai_narrative && (
            <div className="p-3 rounded-lg border border-[#FF2A6D]/40 bg-[#FF2A6D]/5 text-sm text-[#F8FAFC]" data-testid="ai-narrative">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF2A6D] mb-1">// AI Coach</div>
              {aiResults.ai_narrative}
            </div>
          )}
          {aiResults?.recommendations?.map(t => <TrackRow key={t.id} t={t} testid={`ai-rec-${t.id}`}/>)}
          {!aiResults && <div className="text-xs text-[#94A3B8]">Load a track on Deck A, hit play, then ask the AI to pick your next transition.</div>}
        </TabsContent>
      </Tabs>

      {/* YouTube preview modal — nocookie iframe so we don't set 3rd-party
          cookies. Closes on backdrop click or the X button. `autoplay=1`
          starts playback immediately because the modal open itself is a
          user gesture. */}
      {ytPreviewId && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="youtube-preview-modal"
          onClick={() => setYtPreviewId(null)}
        >
          <div
            className="bg-[#121620] border border-[#FF0000]/40 rounded-xl max-w-3xl w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#1E2638]">
              <div className="flex items-center gap-2 min-w-0">
                <Youtube className="w-4 h-4 text-[#FF0000] shrink-0"/>
                <div className="text-sm font-bold truncate" data-testid="youtube-preview-title">{ytPreviewTitle}</div>
              </div>
              <button
                onClick={() => setYtPreviewId(null)}
                data-testid="youtube-preview-close"
                className="text-[#94A3B8] hover:text-white transition-colors shrink-0"
                aria-label="Close preview"
              >
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                data-testid="youtube-preview-iframe"
                src={`https://www.youtube-nocookie.com/embed/${ytPreviewId}?autoplay=1&rel=0`}
                title="YouTube preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>
            <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
              Preview only · To mix this track, find the MP3 and upload it via the Library tab.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
